import { useEffect, useMemo, useReducer, useRef } from 'react'
import useSWR from 'swr'
import { toast } from 'sonner'
import { fetcher } from '@/lib/utils'
import { getTableUrl } from '../constant'
import type { ColumnDef } from '@tanstack/react-table'
import type { SetTableNameToPagination } from '../shared'
import { PaginationClass } from '@/shared'

type Operation = 'LIST' | 'CREATE' | 'UPDATE' | 'DELETE'

export type CRUDState = Record<Operation, boolean>

const initialState: CRUDState = {
  LIST: false,
  CREATE: false,
  UPDATE: false,
  DELETE: false,
}

type Action = { type: 'START' | 'END'; operation: Operation }

function reducer(state: CRUDState, action: Action): CRUDState {
  return {
    ...state,
    [action.operation]: action.type === 'START',
  }
}

interface TableDef {
  name: string
  columns: { name: string }[]
}

type UseCRUDOptions = {
  projectId?: string
  basePath?: string
  tableDef?: TableDef | null
  pagination?: PaginationClass
  setTableNameToPagination: SetTableNameToPagination
}

export type CRUD<T = any> = {
  loading: CRUDState
  create: (data: T) => Promise<unknown>
  update: (id: string | number, data: Partial<T>) => Promise<unknown>
  remove: (id: string | number) => Promise<unknown>
  list: T[]
  pagination: PaginationClass
  columns: ColumnDef<any>[]
  refetchList: any
  selectedTableUrl: null | string
}

export function useCRUD<T = any>({
  projectId,
  tableDef,
  pagination = new PaginationClass(),
  setTableNameToPagination,
  basePath = '/api/me/project',
}: UseCRUDOptions): CRUD<T> {
  const tableDefRef = useRef(tableDef)
  useEffect(() => {
    tableDefRef.current = tableDef
  }, [tableDef])

  const [loading, dispatch] = useReducer(reducer, initialState)
  const getURL = () =>
    `${basePath}/${projectId}/table/${tableDefRef.current?.name}/crud`

  const create = async (data: any) => {
    if (!tableDef) throw new Error('table not exist')
    dispatch({ type: 'START', operation: 'CREATE' })
    try {
      const res = await fetcher(getURL(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data }),
      })
      toast.success('Created successfully')
      return res
    } catch (err) {
      toast.error('Create failed')
    } finally {
      dispatch({ type: 'END', operation: 'CREATE' })
    }
  }

  const update = async (id: string | number, data: any) => {
    if (!tableDef) throw new Error('table not exist')
    dispatch({ type: 'START', operation: 'UPDATE' })
    try {
      const res = await fetcher(`${getURL()}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data, id }),
      })
      toast.success('Updated successfully')
      return res
    } catch (err: any) {
      console.error('update error', err.message)
      toast.error('Update failed')
    } finally {
      dispatch({ type: 'END', operation: 'UPDATE' })
    }
  }

  const remove = async (id: string | number) => {
    if (!tableDef) throw new Error('table not exist')
    dispatch({ type: 'START', operation: 'DELETE' })
    try {
      const res = await fetch(`${getURL()}/${id}`, {
        method: 'DELETE',
      })
      toast.success('Deleted successfully')
      return res
    } catch (err) {
      toast.error('Delete failed')
    } finally {
      dispatch({ type: 'END', operation: 'DELETE' })
    }
  }

  const selectedTableUrl = useMemo(() => {
    if (!projectId || !tableDef) return null
    return getTableUrl(projectId, tableDef.name, pagination.page)
  }, [projectId, tableDef, pagination.page])

  const {
    data: listResponse,
    isLoading: isListLoading,
    mutate: refetchList,
  } = useSWR<{
    data: T[]
    page: number
    perPage: number
    total: number
  }>(selectedTableUrl, fetcher)

  useEffect(() => {
    if (isListLoading) {
      dispatch({ type: 'START', operation: 'LIST' })
    } else {
      dispatch({ type: 'END', operation: 'LIST' })
    }
  }, [isListLoading])

  useEffect(() => {
    if (listResponse && tableDef) {
      setTableNameToPagination((prev) => ({
        ...prev,
        [tableDef?.name]: new PaginationClass(
          listResponse.page,
          listResponse.perPage,
          listResponse.total,
        ),
      }))
    }
  }, [listResponse, tableDef, setTableNameToPagination])

  const columns = useMemo(() => {
    if (!tableDef?.columns) return []
    return tableDef.columns.map((col) => ({
      header: () => <div className="text-left">{col.name}</div>,
      accessorKey: col.name,
    }))
  }, [tableDef?.columns])

  return {
    loading,
    create,
    update,
    remove,
    list: listResponse?.data || [],
    selectedTableUrl,
    refetchList,
    pagination,
    columns,
  }
}
